import React, { useCallback, useState, useEffect } from 'react';
import { useTranslator } from '@jutro/locale';
import _ from 'lodash';
import moment from 'moment';
import { ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import PropTypes from 'prop-types';
import { MLAgentPolicySummaryHeaderComponent } from 'gw-capability-quoteandbind-common-react';
import PolicySummaryVehiclesComponent from '../PolicySummaryVehiclesComponent/PolicySummaryVehiclesComponent';
import metadata from './PAPolicySummaryComponent.metadata.json5';
import messages from './PAPolicySummaryComponent.messages';
import styles from './PAPolicySummaryComponent.module.scss';

function initVehicles(policyCoverables, coverages) {
    const vehicles = _.get(policyCoverables, 'vehicles.children', []);
    if (coverages) {
        coverages.vehicleCoverages.forEach((vehicleCov) => {
            const allCovs = coverages.lineCoverages.value.concat(_.get(vehicleCov, 'coverages.value', [])).concat(_.get(coverages, 'optionalCoverages_ML.value', []));

            vehicles.forEach((vehicle) => {
                if (vehicle.fixedId && vehicle.fixedId.value === vehicleCov.fixedId.value) {
                    _.set(vehicle, 'coverages', allCovs);
                    _.set(vehicle, 'vehicleCost_ML', vehicleCov.vehicleCost_ML);
                }
            });
        });
    }
    return vehicles;
}

function PAPolicySummaryComponent(props) {
    const translator = useTranslator();
    const {
        value: policyDetails,
        startPrint,
        updateStartPrint,
        editDrivers,
        editVehicles,
        editCoverages,
        editPayment,
        editEffectiveDate,
        cancelChanges,
        makeMoreChanges,
        submitChanges,
        currentSteps,
        currentStepIndex,
        goToStep,
        selectablePath,
        detailsLoaded,
        ampMode,
        gpaMode,
        qbMode,
        isPolicyChange,
        isModernProduct
    } = props;

    const policyAdditionalInterests = _.get(policyDetails, 'additionalInterests.value', []);
    const quoteId = _.get(policyDetails, 'quoteId.value', '');
    const coverages = _.get(policyDetails, 'coverageLobs.personalAuto', undefined);
    const visitedSteps = currentSteps ? currentSteps.slice(0, currentStepIndex) : undefined;
    const [policyChangeHistories, setPolicyChangeHistories] = useState([]);

    const setPolicyFromPolicyDetails = () => {
        const lobs = _.get(policyDetails, 'lobs.value', undefined);
        const lobsPolChange = _.get(policyDetails, 'lobsPolChange.value', undefined);
        if (lobs && lobs.personalAuto) {
            return policyDetails.lobs.personalAuto;
        }
        if (lobsPolChange && lobsPolChange.personalAuto) {
            return policyDetails.lobsPolChange.personalAuto;
        }
    };

    const policy = setPolicyFromPolicyDetails();
    const policyCoverables = _.get(policy, 'coverables', undefined);
    const vehicles = initVehicles(policyCoverables, coverages);

    const isNamedNonOwner = vehicles.some((vehicle) => {
        return vehicle.namedNonOwner_ML.value === true;
    });

    const resolvers = {
        resolveComponentMap: {
            policySummaryVehiclesComponent: PolicySummaryVehiclesComponent,
            MLAgentPolicySummaryHeaderComponent: MLAgentPolicySummaryHeaderComponent,
        },
        resolveClassNameMap: styles
    };

    useEffect(() => {
        // get all steps, and then filter by the current steps
        if(ampMode && isPolicyChange && visitedSteps){
            const selectableSteps = visitedSteps.filter((policyChangeHistory) => {
                if(policyChangeHistory.path !== '/additionalchanges'){
                    return selectablePath[policyChangeHistory.path];
                }
                return null
            })
            setPolicyChangeHistories(selectableSteps)
        }
    },[])

    const generateOverrides = () => {
        const policyChangeHistoriesOverrides = policyChangeHistories.map((policyChangeHistory, index) => {
            return {
                [`policyChangeHistoryDiv${index}`]: {
                    className: (policyChangeHistories.length - 1) === index ? 'vertical-spacing-40' : 'vertical-spacing-20'
                },
                [`endorsementChangeName${index}`]: {
                    content: translator(selectablePath[policyChangeHistory.path])
                },
                [`endorsementTextLink${index}`]: {
                    onClick: () => goToStep(policyChangeHistory.path)
                }

            }
        })

        return Object.assign({}, ...policyChangeHistoriesOverrides);
    }

    const convertLocalDateToDate = useCallback((localDate) => {
        const parsedDate = new Date(localDate.year, localDate.month, localDate.day);
        return moment(parsedDate).format('MM/DD/YYYY');
    }, []);

    const overrideProps = {
        policySummaryInsuredInfoComponent: {
            model: policyDetails,
            visible: !gpaMode
        },
        agentPolicySummaryComponent: {
            visible: gpaMode,
            model: policyDetails,
            isNamedNonOwner: isNamedNonOwner,
            isModernProduct
        },
        policyHeader: {
            content: ampMode && isPolicyChange ? translator(messages.policyX, { policyId: (_.get(policyDetails, 'policyId.value', ''))}) : translator(messages.quoteX, { quoteId: (quoteId) }),
            visible: !gpaMode
        },
        summaryAdditionalInterestsContainer: {
            visible: (isNamedNonOwner === false) && (policyAdditionalInterests.length > 0) && !gpaMode
        },
        policySummaryAdditionalInterestComponent: {
            model: policyDetails,
            gpaMode: gpaMode
        },
        hrLine: {
            visible: !gpaMode && isPolicyChange
        },
        hrLine1: {
            visible: (isNamedNonOwner === false) && (policyAdditionalInterests.length > 0) && !gpaMode
        },
        ampChangeEffectiveDateContainer: {
            visible: ampMode && isPolicyChange
        },
        changeEffectiveDateSectionHeader: {
            content: translator(messages.changeEffectiveDate, {date: convertLocalDateToDate(_.get(policyDetails, 'effective_ML.value', ''))})
        },
        policyChangeHistoryIterable: {
            data: policyChangeHistories
        },
        policySummaryConfirmationBoxComponent: {
            visible: isPolicyChange && !gpaMode || qbMode,
            ampMode,
            qbMode,
            model: policyDetails,
            startPrint,
            updateStartPrint,
            editPayment,
            editEffectiveDate,
            cancelChanges,
            makeMoreChanges,
            submitChanges,
            detailsLoaded
        },
        policySummaryDriversComponent: {
            model: policyCoverables,
            editDrivers,
            gpaMode: gpaMode,
            isPolicyChange,
            isModernProduct
        },
        policySummaryVehiclesComponent: {
            model: _.get(policy, 'coverables.vehicles.children', []),
            isNamedNonOwner: isNamedNonOwner,
            coverages: _.get(coverages, 'value', undefined),
            termType: _.get(policyDetails, 'termType_ML.value', undefined),
            editVehicles,
            editCoverages,
            gpaMode: gpaMode,
            isPolicyChange,
            isModernProduct
        },
        hrLine3: {
            visible: isPolicyChange
        },
        ampInsuranceCoverageText: {
            visible: ampMode
        },
        ampPolicySummaryConfirmationBoxComponent: {
            visible: isPolicyChange && ampMode,
            model: policyDetails,
            ampMode: ampMode,
            startPrint,
            updateStartPrint,
            editPayment,
            editEffectiveDate,
            cancelChanges,
            makeMoreChanges,
            submitChanges,
            detailsLoaded
        },
        includedFeaturesComponent: {
            model: _.get(policyDetails, 'coverageLobs.personalAuto', undefined)
        },
        agentPolicySummaryInsuredInfo: {
            visible: gpaMode,
            content:
                (
                    <div>
                        <div className='ml-policy-summary-insured-info-gpa col-md-4 vertical-spacing-20'>
                            <span className='eyebrow-header'>
                                {translator(messages.insured)}
                            </span>
                            <ul className='styled-ul-list-body-copy'>
                                <li className='font-weight-bold'>
                                    {_.startCase(_.lowerCase(policyDetails.policyHolder.displayFullName_Ext.value))}
                                </li>
                                {(policyDetails.mailingAddress.addressLine1.value) &&
                                    <li>{policyDetails.mailingAddress.addressLine1.value}</li>
                                }

                                {(policyDetails.mailingAddress.addressLine2.value) && <li> {policyDetails.mailingAddress.addressLine2.value}    </li>

                                }

                                {(policyDetails.mailingAddress.addressLine3.value) && <li> {policyDetails.mailingAddress.addressLine3.value} </li>

                                }
                                <li>
                                    {policyDetails.mailingAddress.city.value}, {policyDetails.mailingAddress.state.value.code}  {policyDetails.mailingAddress.postalCode.value}
                                </li>
                            </ul>
                        </div>
                        {(policyDetails.secondaryInsured_ML.value) &&
                            <div classssName='ml-policy-summary-insured-info-gpa col-md-4 vertical-spacing-20'>

                                <span className='eyebrow-header'>
                                    {translator(messages.coinsured)}
                                </span>
                                <ul className='styled-ul-list-body-copy'>
                                    <li className='font-weight-bold'>
                                        {policyDetails.secondaryInsured_ML.displayName.value}
                                    </li>
                                    {(policyDetails.secondaryInsured_ML.address.addressLine1.value) &&
                                        <li>{policyDetails.secondaryInsured_ML.address.addressLine1.value}</li>
                                    }

                                    {(policyDetails.secondaryInsured_ML.address.addressLine2.value) && <li> {policyDetails.secondaryInsured_ML.address.addressLine2.value}    </li>

                                    }

                                    {(policyDetails.secondaryInsured_ML.address.addressLine3.value) && <li> {policyDetails.secondaryInsured_ML.address.addressLine3.value} </li>

                                    }
                                    <li>
                                        {policyDetails.secondaryInsured_ML.address.city.value}, {policyDetails.secondaryInsured_ML.address.state.value.code}  {policyDetails.secondaryInsured_ML.address.postalCode.value}
                                    </li>
                                </ul>

                            </div>
                        }

                    </div>
                )


        },
        billingSummary: {
            visible: gpaMode,
            content: (
                <div className='vertical-spacing-40'>
                    <h2 className='section-header vertical-spacing-20'>
                        Billing
                    </h2>
                    <hr />
                    <p><strong>{policyDetails.paymentDetails.paymentMethod.value}</strong></p>
                    <hr />
                </div>
            )

        },
        discountsContainer :{
            visible : gpaMode && policyDetails.discounts.value && policyDetails.discounts.length  > 0
        },
        discountIterable : {
            data : policyDetails.discounts.value
        },
        ...generateOverrides()
    };

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            overrideProps={overrideProps}
            model={policyDetails}
            componentMap={resolvers.resolveComponentMap}
            classNameMap={resolvers.resolveClassNameMap}
        />
    );
}

PAPolicySummaryComponent.propTypes = {
    value: PropTypes.shape({}).isRequired,
    startPrint: PropTypes.bool,
    updateStartPrint: PropTypes.func,
    editDrivers: PropTypes.func.isRequired,
    editVehicles: PropTypes.func.isRequired,
    editCoverages: PropTypes.func.isRequired,
    editPayment: PropTypes.func.isRequired,
    gpaMode: PropTypes.bool,
    isPolicyChange: PropTypes.bool,
    isModernProduct: PropTypes.bool
};

PAPolicySummaryComponent.defaultProps = {
    startPrint: false,
    updateStartPrint: _.noop,
    gpaMode: false,
    qbMode: false,
    ampMode: false,
    isPolicyChange: false,
    isModernProduct: false
};

export default PAPolicySummaryComponent;